import { Injectable, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GroupedOffersDataModel, OfferRequest, OfferResponse, RequestBodyInterface } from '@dr-customer-offers-ui/lib-interfaces';
import { OfferService } from '@dr-customer-offers-ui/lib-services';
import { Store } from '@ngrx/store';
import { DetailTableCellModel, MinuteFrequencyOption, NgxIntervalDataGridRowModel, NgxIntervalDataGridService } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import { Timezone } from 'common-utils/dist/models/time';
import moment from 'moment';
import { DatePickerEnum, NgxDateRangePickerOutput } from 'ngx-date-range-picker';
import { NgxDropdownOption } from 'ngx-dropdown';
import { EMPTY, filter, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { BulkUploadComponent } from '../../components/dialogs/bulk-upload/bulk-upload.component';
import { ExportReportComponent } from '../../components/dialogs/export/export-report.component';
import { FiltersStateService } from '../../components/filters/filters-state.service';
import { AppState } from '../../store';
import * as Selectors from '../../store/selectors/com.selectors';
import { DayOfWeekModel, ExportReportOutput, RegConfig, TabOption, TabOptionsEnum } from '../models';
import { BulkUploadDialogInput } from '../models/bulk-upload.model';
import { DaterangeISO } from '../models/daterange';
import { ExportCSVService } from './csv-export.service';
import { InternalService } from './internal.service';

@Injectable({ providedIn: 'root' })
export class DataViewModelService {
  public currentRegViewConfig$: Observable<RegConfig | null> = this.store.select(Selectors.selectCurrentRegViewConfig);
  public externalUserSignal: WritableSignal<boolean> = signal(false);
  selectedTabOptionSignal: WritableSignal<TabOption>;
  formFilter: FormGroup;

  protected tabOptions: TabOption[] = [
    {
      show: true,
      route: 'open',
      name: this.translate.instant('COM.TABS.OPEN.TAB_TITLE'),
      value: TabOptionsEnum.Open,
      selected: true,
      descriptionTitle: this.translate.instant('COM.TABS.OPEN.TITLE'),
      descriptionText: this.translate.instant('COM.TABS.LABELS.GENERAL_DESCRIPTION'),
      showEditButton: true,
      bulkFunctionality: true,
      exportFunctionality: true,
      optOut: true
    }
  ];

  constructor(
    private store: Store<AppState>,
    private internalState: InternalService,
    private offerService: OfferService,
    private ngxService: NgxIntervalDataGridService,
    private fb: FormBuilder,
    private translate: TranslateService,
    public dialog: MatDialog,
    private exportCSVService: ExportCSVService,
    private filtersStateService: FiltersStateService
  ) {
    this.formFilter = this.fb.group({
      filteredFromDayPicker: new FormControl<NgxDateRangePickerOutput | null>(null),
      filteredFromWeekPicker: new FormControl<NgxDateRangePickerOutput | null>(null),
      filteredProgram: new FormControl<NgxDropdownOption | null>(null),
      filteredReg: new FormControl<NgxDropdownOption | null>(null)
    });
    this.selectedTabOptionSignal = signal<TabOption>(this.tabOptions[0]);
  }

  get selectedFromDayPickerFormControl(): FormControl {
    return this.formFilter.get('filteredFromDayPicker') as FormControl;
  }

  get selectedFromWeekPickerFormControl(): FormControl {
    return this.formFilter.get('filteredFromWeekPicker') as FormControl;
  }

  get selectedProgramFormControl(): FormControl {
    return this.formFilter.get('filteredProgram') as FormControl;
  }

  get selectedRegFormControl(): FormControl {
    return this.formFilter.get('filteredReg') as FormControl;
  }

  hasPendingFilterChange(): boolean {
    const pending = this.filtersStateService.pendingFilters();
    const applied = this.filtersStateService.appliedFilters();
    if (!pending || !applied) return false;
    if (pending.registrationId !== applied.registrationId) return true;
    if (pending.datePickerType !== applied.datePickerType) return true;
    return !moment(pending.dateRangeStart).isSame(applied.dateRangeStart, 'day') || !moment(pending.dateRangeEnd).isSame(applied.dateRangeEnd, 'day');
  }

  getAppliedWeekRange(): NgxDateRangePickerOutput | null {
    const applied = this.filtersStateService.appliedFilters();
    if (applied && applied.datePickerType === DatePickerEnum.WeekPicker) {
      return { start: moment(applied.dateRangeStart), end: moment(applied.dateRangeEnd) };
    }
    return this.selectedFromWeekPickerFormControl.value;
  }

  getAppliedDayRange(): NgxDateRangePickerOutput | null {
    const applied = this.filtersStateService.appliedFilters();
    if (applied && applied.datePickerType === DatePickerEnum.DatePicker) {
      return { start: moment(applied.dateRangeStart), end: moment(applied.dateRangeEnd) };
    }
    return this.selectedFromDayPickerFormControl.value;
  }

  featureFlagOnDevAlone(): boolean {
    const fullUrl = window.location.href;
    return (
      fullUrl.includes('customer-offers.dev-apps.enelx.net') ||
      fullUrl.includes('customer-offers.preprd-apps.enelx.com') ||
      fullUrl.includes('local.dev-apps.enelx.net:4400') ||
      fullUrl.includes('local.preprd-apps.enelx.com:4400')
    );
  }

  putGraphDataOffer(body: RequestBodyInterface[]): Observable<any> {
    return this.currentRegViewConfig$.pipe(
      switchMap((regConfig: RegConfig | null) => {
        if (!regConfig) return EMPTY;

        // Transform dates to UTC
        const utcBody = body.map((offer) => ({
          ...offer,
          offer_start_dttm_utc: moment.utc(offer.offer_start_dttm_utc).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          offer_end_dttm_utc: moment.utc(offer.offer_end_dttm_utc).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        }));

        return this.offerService.putGraphDataOffers(regConfig.registrationId, utcBody);
      })
    );
  }

  getExportOffers(dateRange: ExportReportOutput): Observable<{ offers: OfferResponse[] | null; timeZone: string } | null> {
    return this.internalState.selectedRegViewConfigFilterState.pipe(
      switchMap((regConfig: RegConfig | null) => {
        if (!regConfig || !dateRange) return of(null);
        const tz = new Timezone(regConfig?.timeZone ?? 'UTC');
        const startDate: moment.Moment = moment(dateRange.startMomentDate);
        const endDate: moment.Moment = moment(dateRange.endMomentDate);
        const shortTz = tz.getOffset(startDate.format());

        const selectedDatesISO: DaterangeISO = {
          start: moment.utc(startDate.utcOffset(shortTz.stringOffset, true)).toISOString(),
          end: moment.utc(endDate.utcOffset(shortTz.stringOffset, true)).toISOString()
        };

        return forkJoin({
          offers: this.offerService.getExportOffers(regConfig.registrationId, selectedDatesISO.start, selectedDatesISO.end),
          timeZone: of(regConfig.timeZone)
        });
      })
    );
  }

  postOffers(offers: OfferRequest[], regId: string): Observable<OfferResponse[] | null> {
    const sortedOffers = [...offers].sort((a, b) => moment.utc(a.offer_start_dttm_utc).valueOf() - moment.utc(b.offer_start_dttm_utc).valueOf());
    return this.offerService.putOffers(regId, sortedOffers).pipe(
      map((offers: OfferResponse[] | null) => {
        return offers as OfferResponse[];
      })
    );
  }

  putGroupedOffers(offers: GroupedOffersDataModel[], regId: string): Observable<OfferResponse[] | null> {
    return this.offerService.putGroupedOffers(regId, offers).pipe(
      map((offers: OfferResponse[] | null) => {
        return offers as OfferResponse[];
      })
    );
  }

  public cancelEditing(dayOfWeekData: DayOfWeekModel): NgxIntervalDataGridRowModel[] {
    this.ngxService.clearUnSavedData();
    return this.ngxService.groupByDayOfWeek(dayOfWeekData.data, dayOfWeekData.timeZone, dayOfWeekData.tableType);
  }

  public lookForDSTDuplication(unsavedOffers: DetailTableCellModel[], intervalFrequency: MinuteFrequencyOption | undefined): DetailTableCellModel[] {
    const duplicateOffers: DetailTableCellModel[] = [];

    unsavedOffers.forEach((offer: DetailTableCellModel) => {
      if (offer.is_dst_add_hr && intervalFrequency !== MinuteFrequencyOption.ONE_DAY) {
        const duplicatedOffer: DetailTableCellModel = { ...offer };

        const startDate = new Date(duplicatedOffer.start_date_time);
        const endDate = new Date(duplicatedOffer.end_date_time);

        startDate.setUTCHours(startDate.getUTCHours() - 1);
        endDate.setUTCHours(endDate.getUTCHours() - 1);

        duplicatedOffer.start_date_time = startDate.toISOString();
        duplicatedOffer.end_date_time = endDate.toISOString();
        duplicateOffers.push(duplicatedOffer);
      }
    });

    // Add the duplicated offers to the original array or handle them separately as needed
    unsavedOffers.push(...duplicateOffers);
    return unsavedOffers;
  }

  openExportFunctionalityDialog(
    minuteFrequency: MinuteFrequencyOption | undefined,
    minDate: moment.Moment | undefined,
    maxDate: moment.Moment | undefined
  ): void {
    const isWideScreen = window.innerWidth > 2050;
    const dialogRef = this.dialog.open(ExportReportComponent, {
      data: {
        minuteFrequency: minuteFrequency,
        minDate: minDate,
        maxDate: maxDate
      },
      width: isWideScreen ? '50rem' : '35.875rem',
      disableClose: false,
      hasBackdrop: true
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((output: ExportReportOutput) => output !== null),
        switchMap((output: ExportReportOutput) => {
          return this.currentRegViewConfig$.pipe(
            switchMap((selectedReg: RegConfig | null) => {
              return this.getExportOffers(output).pipe(
                tap((o: { offers: OfferResponse[] | null; timeZone: string } | null) => {
                  const _selectedReg: NgxDropdownOption = {
                    id: selectedReg?.registrationId ?? '',
                    value: selectedReg?.display_labels ?? ''
                  };
                  this.exportCSVService.exportCSV(
                    output,
                    o?.timeZone,
                    _selectedReg,
                    o?.offers,
                    this.selectedTabOptionSignal().value,
                    selectedReg?.graph_config?.offer_dimensions ?? [],
                    !!selectedReg?.graph_config?.consolidate_dimensions_into_hourly
                  );
                })
              );
            })
          );
        }),
        take(1)
      )
      .subscribe();
  }

  openBulkUploadFunctionalityDialog(input: BulkUploadDialogInput): void {
    this.dialog.open(BulkUploadComponent, {
      data: input,
      width: '47.8rem',
      disableClose: false,
      hasBackdrop: true
    });
  }
}
